import React, { useState, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../shadcn/ui/card";
import { Input } from "../../../../shadcn/ui/input";
import { Label } from "../../../../shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../shadcn/ui/select";
import { Checkbox } from "../../../../shadcn/ui/checkbox";
import { Textarea } from "../../../../shadcn/ui/textarea";
import { Button } from "../../../../shadcn/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../shadcn/ui/tooltip";
import { HelpCircle } from "lucide-react";
import ImageUploadSection from "./ImageUploadSection";
import amenitiesService from "../../../../services/amenitiesService";
import billingService from "../../../../services/billingService";
import { spaceManagementService } from "../../../../services/host-apiServices/spaceManagementService";
import logger from "../../../../utils/logger";


const FieldTip = ({ children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <HelpCircle className="ml-1 w-4 h-4 text-gray-400 cursor-help" />
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-xs">
      {children}
    </TooltipContent>
  </Tooltip>
);

const SpaceTypeAttributesCard = ({
  control,
  errors,
  // Image upload props
  dragActive,
  fileInputRef,
  handleDrag,
  handleDrop,
  handleFileSelect,
  removeImage,
  watchedImages,
  isReadOnly = false,
  lockedCategory = null,
  serviceCategories = [], // Pass from parent (from Redux)
  categoriesLoading = false, // Prop from parent
  forceSubcategoryLoad = null, // Force subcategory loading when editing existing space
}) => {
  const [amenities, setAmenities] = useState([]);
  const [loadingAmenities, setLoadingAmenities] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [categoryObjects, setCategoryObjects] = useState([]);
  const [_spaceTypes, _setSpaceTypes] = useState([]);
  const [_loadingSpaceTypes, _setLoadingSpaceTypes] = useState(false);
  const [lastFetchedCategory, setLastFetchedCategory] = useState(null);

  // Access form state to coordinate cross-field behavior (category → subCategoryId)
  const { watch: watchForm, setValue: setFormValue } = useFormContext?.() || {
    watch: () => undefined,
    setValue: () => {},
  };
  const selectedCategory = watchForm("category");
  const selectedSubCategory = watchForm("subCategory"); // Get subcategory name for display while loading
  const descriptionValue = watchForm("description") || "";

  // Functions defined with useCallback to be stable dependencies
  const fetchAmenities = React.useCallback(async () => {
    try {
      setLoadingAmenities(true);
      const response = await amenitiesService.getAllAmenities("name,asc");

      if (response.status === 200 && response.data) {
        // Handle paginated response structure
        const amenitiesData = response.data.content || [];
        setAmenities(amenitiesData);
      }
    } catch (error) {
      logger.error("Error fetching amenities:", error);
      setAmenities([]);
    } finally {
      setLoadingAmenities(false);
    }
  }, []);

  const fetchCategories = React.useCallback(async () => {
    try {
      setLoadingCategories(true);
      console.time("⏱️ [PERF] fetchCategories");
      logger.log("🔍 [DEBUG] Fetching categories...");
      const response = await billingService.getPublicServiceCategories();
      logger.log("🔍 [DEBUG] Categories API response:", response);

      // handle both array and wrapped data
      const list = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

      logger.log("🔍 [DEBUG] Processed categories list:", list);

      // Store original category objects for subcategory lookup
      setCategoryObjects(list);

      // map to strings expected by the form (WORK/STAY) if objects are returned
      // Exclude PLAY as it uses a different flow
      const normalized = list
        .map(item => (typeof item === "string" ? item : item.code || item.name))
        .filter(Boolean)
        .filter(category => category.toUpperCase() !== "PLAY");

      logger.log("🔍 [DEBUG] Normalized categories for dropdown:", normalized);
      setCategories(normalized);
    } catch (error) {
      logger.error("❌ [ERROR] Error fetching categories (billingService):", error);
      setCategories(["WORK", "STAY"]); // Exclude PLAY as it uses a different flow
      setCategoryObjects([]);
    } finally {
      console.timeEnd("⏱️ [PERF] fetchCategories");
      setLoadingCategories(false);
    }
  }, []);

  const fetchSpaceTypes = React.useCallback(async () => {
    try {
      _setLoadingSpaceTypes(true);
      const response = await spaceManagementService.getSpaceTypes();

      if (response.status === 200 && response.data) {
        _setSpaceTypes(response.data);
        logger.log("Space types fetched:", response.data);
      }
    } catch (error) {
      logger.error("Error fetching space types:", error);
      // Fallback to hardcoded types if API fails
      _setSpaceTypes(["HIGH", "MEDIUM", "LOW"]);
    } finally {
      _setLoadingSpaceTypes(false);
    }
  }, []);

  const fetchSubcategories = React.useCallback(async (selectedCategoryCode, availableCategories) => {
    // Pass availableCategories explicitly to avoid dependency circle or stale closure
    try {
      console.time(`⏱️ [PERF] fetchSubcategories(${selectedCategoryCode})`);
      setLoadingSubcategories(true);
      logger.log("🔍 [DEBUG] fetchSubcategories called with:", selectedCategoryCode);

      let subcats = [];
      if (selectedCategoryCode && availableCategories.length > 0) {
        // Find the category object by matching the code/name with the selected category code
        const matched = availableCategories.find(c => (c.code || c.name) === selectedCategoryCode);
        logger.log("🔍 [DEBUG] Category matching result:", matched);

        if (matched?.id) {
          logger.log("✅ [DEBUG] Found category for subcategories:", matched);
          logger.log("🔍 [DEBUG] Making API call to get subcategories for category ID:", matched.id);

          const resp = await billingService.getServiceSubcategories(matched.id);
          logger.log("🔍 [DEBUG] Subcategories API response:", resp);

          subcats = Array.isArray(resp.data) ? resp.data : Array.isArray(resp.data?.data) ? resp.data.data : [];
          logger.log("✅ [DEBUG] Processed subcategories:", subcats);
        } else {
          logger.log("❌ [DEBUG] No matching category found for:", selectedCategoryCode);
        }
      } else {
        subcats = [];
      }

      logger.log("🔍 [DEBUG] Setting subcategories to:", subcats);
      setSubcategories(subcats);
    } catch (error) {
      logger.error("❌ [ERROR] Error fetching subcategories (billingService):", error);
      setSubcategories([]);
    } finally {
      console.timeEnd(`⏱️ [PERF] fetchSubcategories(${selectedCategoryCode})`);
      console.timeEnd(`⏱️ [PERF] Category Switch to ${selectedCategoryCode}`);
      setLoadingSubcategories(false);
    }
  }, []);

  // Use serviceCategories from parent if available (from Redux cache)
  useEffect(() => {
    if (serviceCategories && serviceCategories.length > 0) {
      setCategoryObjects(serviceCategories);
      const normalized = serviceCategories
        .map(item => item.code || item.name)
        .filter(Boolean)
        .filter(category => category.toUpperCase() !== "PLAY");
      setCategories(normalized);
      setLoadingCategories(false);
    }
  }, [serviceCategories]);

  useEffect(() => {
    fetchAmenities();
    fetchSpaceTypes();
  }, [fetchAmenities, fetchSpaceTypes]);

  // Single useEffect to handle subcategory fetching
  useEffect(() => {
    if (!selectedCategory || categoryObjects.length === 0) {
      setSubcategories([]);
      return;
    }

    // Prevent duplicate calls
    if (lastFetchedCategory === selectedCategory) return;

    console.time(`⏱️ [PERF] Category Switch to ${selectedCategory}`);
    setLastFetchedCategory(selectedCategory);
    fetchSubcategories(String(selectedCategory).toUpperCase(), categoryObjects);
  }, [selectedCategory, categoryObjects, lastFetchedCategory, fetchSubcategories]);

  // Force subcategory loading when editing existing space (forceSubcategoryLoad is set after form reset)
  useEffect(() => {
    if (forceSubcategoryLoad && categoryObjects.length > 0) {
      logger.log("🔄 [DEBUG] Force loading subcategories for:", forceSubcategoryLoad);
      // Reset lastFetchedCategory to force a refetch
      setLastFetchedCategory(null);
      fetchSubcategories(String(forceSubcategoryLoad).toUpperCase(), categoryObjects);
    }
  }, [forceSubcategoryLoad, categoryObjects, fetchSubcategories]);

  // Determine if categories are loading (either from parent or internal fetch)
  const isCategoriesLoading = categoriesLoading || loadingCategories;

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-xl">Space Type & Attributes</CardTitle>
          </div>
          <CardDescription className="mt-1 text-sm text-gray-500">What kind of space is being uploaded</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <TooltipProvider>
          {/* Category - Hidden when locked */}
          {!lockedCategory && (
            <div>
              <Label htmlFor="category" className="flex items-center mb-1">
                Category{" "}
                <FieldTip>
                  The primary type of space — Work, Stay, or Play. Determines booking rules and marketplace listing.
                </FieldTip>
              </Label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={value => {
                      logger.log("🔍 [DEBUG] Category selected:", value);
                      field.onChange(value);

                      const selectedCategoryObj = categoryObjects.find(c => (c.code || c.name) === value);
                      if (selectedCategoryObj?.id) {
                        logger.log("🔍 [DEBUG] Setting categoryId:", selectedCategoryObj.id);
                        setFormValue("categoryId", selectedCategoryObj.id, {
                          shouldValidate: false,
                        });
                      }

                      setFormValue("subCategoryId", undefined, {
                        shouldValidate: false,
                      });
                    }}
                    disabled={isReadOnly || isCategoriesLoading}
                  >
                    <SelectTrigger id="category">
                      <SelectValue placeholder={isCategoriesLoading ? "Loading categories..." : "Select category"} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(category => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                      {categories.length === 0 && !loadingCategories && (
                        <SelectItem value="no-categories" disabled>
                          No categories found
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.category && <p className="mt-1 text-sm text-red-500">{errors.category.message}</p>}
            </div>
          )}

          {/* Subcategory */}
          <div>
            <Label htmlFor="subcategory" className="flex items-center mb-1">
              Subcategory{" "}
              <FieldTip>
                A more specific classification within the selected category (e.g. Conference Room under Work).
              </FieldTip>
            </Label>
            <Controller
              name="subCategoryId"
              control={control}
              render={({ field }) => {
                // Check if we have a value but no matching option in subcategories
                const hasMatchingOption = subcategories.some(s => s.id === field.value);
                // Show subcategory name while loading OR when we have a value but options aren't loaded yet
                const showLoadingWithValue =
                  field.value &&
                  selectedSubCategory &&
                  (loadingSubcategories || (!hasMatchingOption && subcategories.length === 0));

                return showLoadingWithValue ? (
                  // Show current subcategory name while subcategories are loading
                  <div className="h-10 px-3 flex items-center border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-600">
                    <span>{selectedSubCategory}</span>
                    <span className="ml-2 text-xs text-gray-400">(loading...)</span>
                  </div>
                ) : (
                  <Select
                    value={field.value ? field.value.toString() : ""}
                    onValueChange={value => field.onChange(parseInt(value))}
                    disabled={isReadOnly || loadingSubcategories}
                  >
                    <SelectTrigger id="subcategory">
                      <SelectValue
                        placeholder={loadingSubcategories ? "Loading subcategories..." : "Select subcategory"}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {subcategories.map(subcategory => (
                        <SelectItem key={subcategory.id} value={subcategory.id.toString()}>
                          {subcategory.name}
                        </SelectItem>
                      ))}
                      {subcategories.length === 0 && !loadingSubcategories && (
                        <SelectItem value="no-subcategories" disabled>
                          No subcategories found
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                );
              }}
            />
            {errors.subCategoryId && <p className="mt-1 text-sm text-red-500">{errors.subCategoryId.message}</p>}
          </div>

          {/* Capacity */}
          <div>
            <Label htmlFor="capacity" className="flex items-center mb-1">
              Capacity <FieldTip>The maximum number of people this space can comfortably accommodate.</FieldTip>
            </Label>
            <Controller
              name="capacity"
              control={control}
              render={({ field }) => (
                <Input id="capacity" placeholder="Number of people" type="number" disabled={isReadOnly} {...field} />
              )}
            />
            {errors.capacity && <p className="mt-1 text-sm text-red-500">{errors.capacity.message}</p>}
          </div>

          {/* Size */}
          <div>
            <Label htmlFor="size" className="flex items-center mb-1">
              Size{" "}
              <FieldTip>
                The total floor area of the space (e.g. 40 sqm). Helps customers understand the scale.
              </FieldTip>
            </Label>
            <Controller
              name="size"
              control={control}
              render={({ field }) => (
                <Input id="size" placeholder="e.g., 40 sqm or 400 sq ft" disabled={isReadOnly} {...field} />
              )}
            />
            {errors.size && <p className="mt-1 text-sm text-red-500">{errors.size.message}</p>}
          </div>

          {/* Amenities */}
          <div>
            <Label className="flex items-center mb-2">
              Amenities{" "}
              <FieldTip>
                Select features and facilities available in this space. Managed in Settings → Amenities Management.
              </FieldTip>
            </Label>
            {loadingAmenities ? (
              <div className="flex justify-center items-center py-4">
                <div className="w-6 h-6 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                <span className="ml-2 text-sm text-gray-500">Loading amenities...</span>
              </div>
            ) : (
              <div className="grid overflow-y-auto grid-cols-2 gap-4 max-h-60">
                <Controller
                  name="amenities"
                  control={control}
                  render={({ field }) => (
                    <>
                      {amenities.map(amenity => (
                        <div key={amenity.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`amenity-${amenity.id}`}
                            checked={field.value.includes(amenity.id)}
                            disabled={isReadOnly}
                            onCheckedChange={checked => {
                              const newValue = checked
                                ? [...field.value, amenity.id]
                                : field.value.filter(id => id !== amenity.id);
                              field.onChange(newValue);
                            }}
                          />
                          <Label htmlFor={`amenity-${amenity.id}`} className="text-sm font-normal">
                            {amenity.label || amenity.name}
                          </Label>
                        </div>
                      ))}
                      {amenities.length === 0 && !loadingAmenities && (
                        <div className="col-span-2 py-4 text-center">
                          <p className="text-sm text-gray-500">No amenities available</p>
                          <p className="mt-1 text-xs text-gray-400">Add amenities in Settings → Amenities Management</p>
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
            )}
            {errors.amenities && <p className="mt-1 text-sm text-red-500">{errors.amenities.message}</p>}
          </div>

          {/* Description */}
          <div className="relative">
            <Label htmlFor="description" className="flex items-center mb-1">
              Description{" "}
              <FieldTip>Describe your space, its features, and any usage rules. Max 250 characters.</FieldTip>
            </Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Textarea
                  id="description"
                  placeholder="Describe the space, its features and any usage rules..."
                  rows={4}
                  disabled={isReadOnly}
                  maxLength={250}
                  {...field}
                />
              )}
            />
            <div className="mt-1 text-xs text-gray-500 text-right">{descriptionValue.length}/250 characters</div>
            {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>}
          </div>

          {/* PLAY Service Specific Fields */}
          {selectedCategory === "PLAY" && (
            <div className="space-y-4 border-t pt-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <h3 className="text-lg font-medium text-gray-900">PLAY Service Settings</h3>
              </div>

              {/* Ticket Type */}
              <div>
                <Label htmlFor="ticket-type" className="flex items-center mb-1">
                  Ticket Type{" "}
                  <FieldTip>Only for PLAY services. Specify the ticket tier (e.g. regular, VIP, premium).</FieldTip>
                </Label>
                <Controller
                  name="ticketType"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="ticket-type"
                      placeholder="e.g., test, regular, premium"
                      disabled={isReadOnly}
                      {...field}
                    />
                  )}
                />
                {errors.ticketType && <p className="mt-1 text-sm text-red-500">{errors.ticketType.message}</p>}
              </div>

              {/* Tickets */}
              <div>
                <Label className="flex items-center mb-2">
                  Ticket Configuration{" "}
                  <FieldTip>
                    Only for PLAY services. Configure ticket tiers and their associated URLs or details.
                  </FieldTip>
                </Label>
                <p className="text-xs text-gray-500 mb-3">Configure ticket types and URLs for your PLAY service</p>
                <Controller
                  name="tickets"
                  control={control}
                  render={({ field }) => (
                    <div className="space-y-3">
                      {field.value?.map((ticket, index) => (
                        <div key={index} className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg">
                          <div className="flex-1">
                            <Input
                              placeholder="Ticket type (e.g., OFFLINE, ONLINE)"
                              value={ticket.ticketType || ""}
                              onChange={e => {
                                const newTickets = [...field.value];
                                newTickets[index] = { ...ticket, ticketType: e.target.value };
                                field.onChange(newTickets);
                              }}
                              disabled={isReadOnly}
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              placeholder="Ticket URL (optional)"
                              value={ticket.url || ""}
                              onChange={e => {
                                const newTickets = [...field.value];
                                newTickets[index] = { ...ticket, url: e.target.value };
                                field.onChange(newTickets);
                              }}
                              disabled={isReadOnly}
                            />
                          </div>
                          {!isReadOnly && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newTickets = field.value.filter((_, i) => i !== index);
                                field.onChange(newTickets);
                              }}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      ))}
                      {!isReadOnly && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newTickets = [...(field.value || []), { ticketType: "", url: "" }];
                            field.onChange(newTickets);
                          }}
                        >
                          Add Ticket Type
                        </Button>
                      )}
                    </div>
                  )}
                />
                {errors.tickets && <p className="mt-1 text-sm text-red-500">{errors.tickets.message}</p>}
              </div>
            </div>
          )}

          {/* Image Upload Section */}
          <ImageUploadSection
            watchedImages={watchedImages}
            dragActive={dragActive}
            fileInputRef={fileInputRef}
            handleDrag={handleDrag}
            handleDrop={handleDrop}
            handleFileSelect={handleFileSelect}
            removeImage={removeImage}
            errors={errors}
            isReadOnly={isReadOnly}
          />
        </TooltipProvider>
      </CardContent>
    </Card>
  );
};

export default SpaceTypeAttributesCard;
